import React, { useState, useEffect, useRef } from 'react';
import { X, Copy, CheckCircle, Loader, AlertCircle, Clock, XCircle } from 'lucide-react';
import axios from 'axios';

import { getBackendUrl } from '../utils/api';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const PixCheckoutModal = ({ isOpen, onClose, orderData, subdomain, onSuccess }) => {
  const [qrCode, setQrCode] = useState('');
  const [qrCodeBase64, setQrCodeBase64] = useState('');
  const [paymentId, setPaymentId] = useState('');
  const [orderId, setOrderId] = useState('');
  const [orderNumber, setOrderNumber] = useState('');
  const [copied, setCopied] = useState(false);
  const [status, setStatus] = useState('creating'); // creating, pending, checking, approved, expired, error
  const [error, setError] = useState('');
  const [expiryMinutes, setExpiryMinutes] = useState(15);
  const [timeLeft, setTimeLeft] = useState(null); // seconds remaining
  const pollingRef = useRef(null);
  const countdownRef = useRef(null);
  const expiryTimeRef = useRef(null);

  useEffect(() => {
    if (isOpen && orderData) {
      createOrderAndPayment();
    }
    
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [isOpen, orderData]);

  useEffect(() => {
    if (status === 'checking' && paymentId && orderId) {
      pollingRef.current = setInterval(() => {
        checkPaymentStatus();
      }, 3000);

      return () => {
        if (pollingRef.current) clearInterval(pollingRef.current);
      };
    }
  }, [status, paymentId, orderId]);

  // Countdown timer
  useEffect(() => {
    if (status === 'checking' && expiryTimeRef.current) {
      const updateCountdown = () => {
        const now = Date.now();
        const remaining = Math.max(0, Math.floor((expiryTimeRef.current - now) / 1000));
        setTimeLeft(remaining);
        
        if (remaining <= 0) {
          setStatus('expired');
          if (pollingRef.current) clearInterval(pollingRef.current);
          if (countdownRef.current) clearInterval(countdownRef.current);
        }
      };
      
      updateCountdown();
      countdownRef.current = setInterval(updateCountdown, 1000);
      
      return () => {
        if (countdownRef.current) clearInterval(countdownRef.current);
      };
    }
  }, [status, expiryMinutes]);

  const formatTime = (totalSeconds) => {
    if (totalSeconds === null) return '';
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getTimerColor = () => {
    if (timeLeft === null) return 'text-blue-400';
    if (timeLeft <= 60) return 'text-red-400';
    if (timeLeft <= 180) return 'text-yellow-400';
    return 'text-green-400';
  };

  const createOrderAndPayment = async () => {
    try {
      setStatus('creating');
      setError('');

      // Step 1: Create order in backend
      const orderResponse = await axios.post(
        `${API}/public/menu/${subdomain}/orders`,
        orderData
      );

      if (!orderResponse.data || !orderResponse.data.order_number) {
        throw new Error('Erro ao criar pedido');
      }

      const createdOrderId = orderResponse.data.order_id || orderResponse.data.id;
      const createdOrderNumber = orderResponse.data.order_number;
      setOrderId(createdOrderId);
      setOrderNumber(createdOrderNumber);
      
      // Get expiry info from response
      const respExpiryMin = orderResponse.data.pix_expiry_minutes || 15;
      setExpiryMinutes(respExpiryMin);
      expiryTimeRef.current = Date.now() + (respExpiryMin * 60 * 1000);

      // Step 2: Create PIX payment
      const paymentResponse = await axios.post(
        `${API}/mercadopago/create-pix-payment`,
        {
          order_id: createdOrderId,
          amount: orderData.total,
          description: `Pedido ${orderResponse.data.order_number} - ${orderData.customer_name}`,
          payer_email: orderData.customer_email || 'cliente@email.com'
        }
      );

      if (paymentResponse.data.success) {
        setQrCode(paymentResponse.data.qr_code);
        setQrCodeBase64(paymentResponse.data.qr_code_base64);
        setPaymentId(paymentResponse.data.payment_id);
        setStatus('checking');
      } else {
        setError('Erro ao criar pagamento PIX');
        setStatus('error');
      }
    } catch (err) {
      console.error('Error creating order/payment:', err);
      setError(
        err.response?.data?.detail || 
        'Erro ao processar pedido. A loja pode n\u00E3o ter configurado o Mercado Pago.'
      );
      setStatus('error');
    }
  };

  const checkPaymentStatus = async () => {
    try {
      const response = await axios.get(
        `${API}/mercadopago/payment-status/${paymentId}?order_id=${orderId}`
      );

      if (response.data.status === 'approved') {
        setStatus('approved');
        if (pollingRef.current) clearInterval(pollingRef.current);
        if (countdownRef.current) clearInterval(countdownRef.current);
        
        setTimeout(() => {
          if (onSuccess) {
            const confirmedOrder = {
              ...orderData,
              order_id: orderId,
              order_number: orderNumber,
              payment_status: 'approved',
              payment_method: 'pix_mp'
            };
            onSuccess(confirmedOrder);
          }
        }, 2000);
      }
    } catch (err) {
      console.error('Error checking payment status:', err);
    }
  };

  const copyToClipboard = () => {
    if (qrCode) {
      navigator.clipboard.writeText(qrCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleClose = () => {
    if (pollingRef.current) clearInterval(pollingRef.current);
    if (countdownRef.current) clearInterval(countdownRef.current);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-2xl font-bold text-gray-900" data-testid="pix-checkout-title">Pagamento PIX</h2>
            <p className="text-gray-600 text-sm mt-1">
              R$ {orderData?.total?.toFixed(2)}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            data-testid="pix-checkout-close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {status === 'creating' && (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader className="w-12 h-12 text-blue-500 animate-spin mb-4" />
              <p className="text-gray-700 font-semibold">Gerando pagamento PIX...</p>
              <p className="text-gray-500 text-sm mt-2">Aguarde um momento</p>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 text-center">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-6 h-6 text-red-500" />
              </div>
              <p className="text-red-600 font-semibold mb-2">Erro ao criar pagamento</p>
              <p className="text-gray-600 text-sm">{error}</p>
              <button
                onClick={createOrderAndPayment}
                className="mt-4 px-6 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg transition-colors"
              >
                Tentar Novamente
              </button>
            </div>
          )}

          {status === 'expired' && (
            <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 text-center" data-testid="pix-expired">
              <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <XCircle className="w-10 h-10 text-orange-500" />
              </div>
              <p className="text-orange-600 font-bold text-xl mb-2">Tempo Esgotado</p>
              <p className="text-gray-600 text-sm">
                O prazo para pagamento expirou. Fa&ccedil;a um novo pedido para tentar novamente.
              </p>
              <button
                onClick={handleClose}
                className="mt-4 px-6 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-lg transition-colors font-medium"
              >
                Fechar
              </button>
            </div>
          )}

          {(status === 'checking' || status === 'approved') && (
            <>
              {/* Countdown Timer */}
              {status === 'checking' && timeLeft !== null && (
                <div className="flex items-center justify-center gap-3 py-3 px-4 bg-gray-50 rounded-xl border border-gray-200" data-testid="pix-countdown">
                  <Clock className={`w-5 h-5 ${getTimerColor()}`} />
                  <span className="text-gray-600 text-sm">Tempo restante:</span>
                  <span className={`font-mono font-bold text-xl ${getTimerColor()}`} data-testid="pix-countdown-value">
                    {formatTime(timeLeft)}
                  </span>
                </div>
              )}

              {/* QR Code */}
              {qrCodeBase64 && (
                <div className="flex flex-col items-center">
                  <div className="bg-white p-4 rounded-2xl shadow-xl border-2 border-gray-200">
                    <img
                      src={`data:image/png;base64,${qrCodeBase64}`}
                      alt="QR Code PIX"
                      className="w-64 h-64"
                      data-testid="pix-qr-code"
                    />
                  </div>
                  <p className="text-gray-600 text-sm mt-4 text-center">
                    Escaneie o QR Code com seu aplicativo banc&aacute;rio
                  </p>
                </div>
              )}

              {/* PIX Code */}
              {qrCode && (
                <div className="space-y-3">
                  <label className="block text-sm font-medium text-gray-700">
                    Ou copie o c&oacute;digo PIX:
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={qrCode}
                      readOnly
                      className="flex-1 bg-gray-100 border border-gray-300 rounded-lg px-4 py-3 text-gray-800 text-sm font-mono"
                      data-testid="pix-code-input"
                    />
                    <button
                      onClick={copyToClipboard}
                      className="px-6 py-3 bg-blue-500 hover:bg-blue-600 text-white rounded-lg transition-colors flex items-center gap-2 whitespace-nowrap"
                      data-testid="pix-copy-button"
                    >
                      {copied ? (
                        <>
                          <CheckCircle className="w-5 h-5" />
                          Copiado!
                        </>
                      ) : (
                        <>
                          <Copy className="w-5 h-5" />
                          Copiar
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Status */}
              {status === 'checking' && (
                <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-6 text-center" data-testid="pix-waiting-status">
                  <div className="flex items-center justify-center mb-4">
                    <Loader className="w-8 h-8 text-blue-500 animate-spin" />
                  </div>
                  <p className="text-gray-800 font-semibold mb-2">Aguardando pagamento...</p>
                  <p className="text-gray-600 text-sm">
                    Estamos verificando seu pagamento automaticamente
                  </p>
                </div>
              )}

              {status === 'approved' && (
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-6 text-center" data-testid="pix-approved-status">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <p className="text-green-600 font-bold text-xl mb-2">Pagamento Aprovado!</p>
                  <p className="text-gray-600 text-sm">
                    Seu pedido foi confirmado com sucesso
                  </p>
                </div>
              )}

              {/* Instructions */}
              {status === 'checking' && (
                <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                  <h3 className="text-gray-800 font-semibold text-sm mb-3">Como pagar:</h3>
                  <div className="space-y-2 text-gray-600 text-sm">
                    <p>1. Abra seu aplicativo banc&aacute;rio</p>
                    <p>2. Escolha pagar com PIX</p>
                    <p>3. Escaneie o QR Code ou cole o c&oacute;digo</p>
                    <p>4. Confirme o pagamento</p>
                    <p className="text-green-600 font-semibold mt-3">
                      O pagamento ser&aacute; confirmado automaticamente
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {status !== 'approved' && status !== 'expired' && (
          <div className="p-6 border-t border-gray-200">
            <button
              onClick={handleClose}
              className="w-full px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg transition-colors font-medium"
              data-testid="pix-checkout-cancel"
            >
              Fechar
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PixCheckoutModal;
